import { useState, useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { Sparkle, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { initIAP, getOfferings } from "@/lib/iap";

const ENTITLEMENT_ID = "plus";

/**
 * HushPlusUpgrade — paywall component shown inside SettingsDrawer.
 *
 * Web behaviour: shows "Open in the iOS app to subscribe" (since Apple does
 * not allow paid subscriptions outside of native IAP).
 *
 * Native behaviour: uses @revenuecat/purchases-capacitor to:
 *   - fetch the current offering
 *   - render a Monthly / Annual selector
 *   - call `purchasePackage` which triggers Apple's native sheet
 *   - on success, the RevenueCat webhook will flip server-side `subscription_tier`
 *     to "plus" within ~1s. We also reload /subscriptions/me right after.
 */
export default function HushPlusUpgrade({ user, onChanged }) {
  const isNative = Capacitor.isNativePlatform();
  const [sub, setSub] = useState(null);
  const [offerings, setOfferings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loadingOfferings, setLoadingOfferings] = useState(false);

  const refreshSub = useCallback(async () => {
    try {
      const { data } = await api.get("/subscriptions/me");
      setSub(data);
      if (onChanged) onChanged(data);
    } catch (_) { /* noop */ }
  }, [onChanged]);

  useEffect(() => { refreshSub(); }, [refreshSub, user?.subscription_active, user?.subscription_source]);

  // Load offerings on native — extracted into a function so we can retry on error.
  // CRITICAL: await initIAP() (Purchases.configure) before calling getOfferings,
  // otherwise on a cold app launch the paywall mounts before RC is configured
  // and Apple's reviewer sees "Couldn't load subscription options" (2.1(b) reject).
  const loadOfferings = useCallback(async () => {
    if (!isNative || !user?.user_id) return;
    setLoadingOfferings(true);
    setError("");
    try {
      // Idempotent — short-circuits if already configured.
      const ok = await initIAP(user.user_id);
      if (!ok) {
        setError("Couldn't initialize the store. Tap retry.");
        return;
      }
      // Identify the user for webhook routing.
      try {
        const { Purchases } = await import("@revenuecat/purchases-capacitor");
        await Purchases.logIn({ appUserID: user.user_id });
      } catch (_) { /* logIn is best-effort; getOfferings still works */ }
      const current = await getOfferings();
      if (!current || !current.availablePackages?.length) {
        setError("Couldn't load subscription options. Tap retry.");
        setOfferings(null);
      } else {
        setOfferings(current);
      }
    } catch (e) {
      setError("Couldn't load subscription options. Tap retry.");
      setOfferings(null);
    } finally {
      setLoadingOfferings(false);
    }
  }, [isNative, user?.user_id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadOfferings();
    })();
    return () => { cancelled = true; };
  }, [loadOfferings]);

  const handlePurchase = async (pkg) => {
    if (!isNative) return;
    setBusy(true); setError("");
    try {
      const { Purchases } = await import("@revenuecat/purchases-capacitor");
      const res = await Purchases.purchasePackage({ aPackage: pkg });
      const active = res?.customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
      if (active) {
        // Webhook will mirror server-side; refresh once.
        await new Promise((r) => setTimeout(r, 1200));
        await refreshSub();
      } else {
        setError("Purchase didn't activate your subscription. Try Restore Purchases.");
      }
    } catch (e) {
      const code = e?.code || e?.errorCode;
      if (code === "PURCHASE_CANCELLED_ERROR" || /cancel/i.test(String(e?.message))) {
        // user cancelled — no error UI
      } else {
        setError(String(e?.message || "Purchase failed."));
      }
    } finally { setBusy(false); }
  };

  const handleRestore = async () => {
    if (!isNative) return;
    setBusy(true); setError("");
    try {
      const { Purchases } = await import("@revenuecat/purchases-capacitor");
      const customerInfo = await Purchases.restorePurchases();
      const active = customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
      if (!active) setError("No active subscription found for this Apple ID.");
      await new Promise((r) => setTimeout(r, 800));
      await refreshSub();
    } catch (e) {
      setError("Couldn't restore purchases.");
    } finally { setBusy(false); }
  };

  // Already Plus
  if (sub?.active) {
    const viaFounder = sub.source === "founder";
    const viaReferral = sub.source === "referral";
    let titleText = "You're on Hush Plus";
    if (viaFounder) titleText = `Founder #${sub.founder_number || ""}`;
    else if (viaReferral) titleText = "You've got Hush Plus, free.";
    let subText;
    if (viaFounder) {
      subText = "Hush Plus for life · thank you for being one of the first.";
    } else if (viaReferral) {
      subText = `Earned via referral credit${sub.current_period_end ? ` · until ${new Date(sub.current_period_end).toLocaleDateString()}` : ""}`;
    } else {
      subText = `${sub.product_id === "hush_plus_annual" ? "Annual plan" : "Monthly plan"}${sub.current_period_end ? ` · renews ${new Date(sub.current_period_end).toLocaleDateString()}` : ""}`;
    }
    return (
      <div className="card p-5 border-brand/30 bg-brand/[0.04]" data-testid="hush-plus-active">
        <div className="flex items-center gap-3 mb-2">
          <CheckCircle size={22} weight="fill" className="text-brand" />
          <p className="font-serif text-lg">{titleText}</p>
        </div>
        <p className="text-sm text-ink-500">{subText}</p>
      </div>
    );
  }

  // Free — show the upgrade pitch
  const monthly = offerings?.availablePackages?.find?.((p) => p.identifier === "$rc_monthly" || /month/i.test(p.identifier));
  const annual = offerings?.availablePackages?.find?.((p) => p.identifier === "$rc_annual" || /annual|year/i.test(p.identifier));

  return (
    <div className="card p-6 grain border-brand/30 bg-brand/[0.04]" data-testid="hush-plus-upgrade">
      <div className="flex items-center gap-2 mb-2">
        <Sparkle size={22} weight="fill" className="text-brand" />
        <p className="eyebrow text-brand">Hush Plus</p>
      </div>
      <h3 className="font-serif text-xl text-ink-900 mb-3">
        Unlimited reflection, every week.
      </h3>
      <ul className="space-y-2 text-[14px] text-ink-700 leading-relaxed mb-5">
        <li className="flex items-start gap-2">
          <CheckCircle size={16} weight="fill" className="text-brand shrink-0 mt-1" />
          <span><b>Unlimited entry length</b> — speak as long as you like (free is capped at 3 minutes).</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle size={16} weight="fill" className="text-brand shrink-0 mt-1" />
          <span><b>Deep weekly reflection</b> — a longer, written-back letter from Hush every Sunday morning, in-app and by email.</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle size={16} weight="fill" className="text-brand shrink-0 mt-1" />
          <span><b>Cancel anytime.</b> 7-day free trial on your first subscription.</span>
        </li>
      </ul>

      {!isNative && (
        <div className="rounded-lg bg-bone-200 p-4 text-sm text-ink-700">
          Hush Plus is purchased inside the mobile app.{" "}
          <a href="https://apps.apple.com/app/id6769325168" target="_blank" rel="noreferrer" className="text-brand underline">
            iPhone / iPad
          </a>
          {" · "}
          <a href="https://play.google.com/store/apps/details?id=app.hush.journal" target="_blank" rel="noreferrer" className="text-brand underline">
            Android
          </a>
          {" "}→ Settings → Hush Plus to subscribe.
        </div>
      )}

      {isNative && offerings && (
        <div className="flex flex-col gap-2">
          {annual && (
            <div className="relative">
              {/* "Most Popular · Save 33%" ribbon — classic conversion lift for
                  annual subscriptions. Math: 12 × $4.99 = $59.88 vs $39.99 = 33% off.
                  We only show the badge if BOTH monthly + annual exist (otherwise
                  "Save 33%" makes no sense). */}
              {monthly && (
                <div
                  className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10 bg-moss text-bone-100 text-[10px] tracking-[0.18em] font-medium uppercase px-3 py-1 rounded-full shadow-sm whitespace-nowrap"
                  data-testid="annual-popular-ribbon"
                >
                  Most popular · Save 33%
                </div>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => handlePurchase(annual)}
                className="btn-primary w-full disabled:opacity-50"
                data-testid="purchase-annual-btn"
              >
                {busy ? "…" : `Try free — then ${annual.product?.priceString || "$39.99"}/year`}
              </button>
            </div>
          )}
          {monthly && (
            <button
              type="button"
              disabled={busy}
              onClick={() => handlePurchase(monthly)}
              className="btn-ghost w-full disabled:opacity-50"
              data-testid="purchase-monthly-btn"
            >
              {busy ? "…" : `Or ${monthly.product?.priceString || "$4.99"}/month`}
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={handleRestore}
            className="text-xs text-ink-500 underline mt-2 inline-flex items-center gap-1 self-center"
            data-testid="restore-purchases-btn"
          >
            <ArrowsClockwise size={12} /> Restore purchases
          </button>
        </div>
      )}

      {isNative && !offerings && !error && (
        <p className="text-sm text-ink-500">Loading plans…</p>
      )}

      {error && (
        <div className="mt-3 space-y-2">
          <p className="text-sm text-brand bg-brand/10 border border-brand/20 rounded-md px-3 py-2">
            {error}
          </p>
          {isNative && (
            <button
              type="button"
              onClick={loadOfferings}
              disabled={loadingOfferings}
              className="btn-ghost w-full text-sm disabled:opacity-50"
              data-testid="retry-load-offerings-btn"
            >
              {loadingOfferings ? "Retrying…" : "Retry"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
